import moment from 'moment';
import { UserEmailConfiguration } from '../../../../NotificationCenter/NotificationEmailConfiguration';
import { getSurgeonDisplayName } from '../../../../../util/surgeonDisplayName';

interface AddressData {
  streetAddress1?: string;
  streetAddress2?: string;
  city?: string;
  country?: string;
  state?: string;
  zipCode?: string;
}

interface BusinessRule {
  rules: string;
  values?: string;
}

interface SalesRepInfo {
  firstName?: string;
  lastName?: string;
  email?: string;
  businessRules?: BusinessRule[];
  [key: string]: any;
}

const ADDRESS_FIELD_MAP: Record<string, keyof AddressData> = {
  'Street Address 1': 'streetAddress1',
  'Street Address 2': 'streetAddress2',
  'City': 'city',
  'Country': 'country',
  'State': 'state',
  'Zip Code': 'zipCode',
};

function extractAddressFromBusinessRules(businessRules: BusinessRule[]): AddressData {
  const addressData: AddressData = {};

  businessRules.forEach((rule) => {
    const fieldKey = ADDRESS_FIELD_MAP[rule.rules];
    if (fieldKey) {
      addressData[fieldKey] = rule.values || '';
    }
  });

  return addressData;
}

function formatAddressLines(addressData: AddressData): string[] {
  const lines: string[] = [];

  const addressPart1 = `${addressData.streetAddress1 ?? ''} ${addressData.streetAddress2 ?? ''}`.trim();
  if (addressPart1) {
    lines.push(addressPart1);
  }

  const addressPart2 = `${addressData.city ?? ''} ${addressData.country ?? ''} ${addressData.state ?? ''} ${addressData.zipCode ?? ''}`.trim();
  if (addressPart2) {
    lines.push(addressPart2);
  }

  return lines;
}

function generateBasicOrderInfo(
  accountNumberData: string,
  accountName: string,
  usageDetails: any
): string {
  const orderNumber = usageDetails?.eventId || usageDetails?.caseNumber || '';
  const poNumber = usageDetails?.poNumber ? `Purchase Order #: ${usageDetails.poNumber}\n` : '';
  const surgeryDate = usageDetails?.eventDateTime
    ? `Surgery Date: ${moment(new Date(usageDetails.eventDateTime)).format('MM/DD/YYYY')}\n`
    : '';
  const surgeonName = getSurgeonDisplayName(usageDetails);
  const surgeon = surgeonName && surgeonName !== '--' ? `Surgeon: ${surgeonName}\n` : '';
  const procedure = usageDetails?.procedureName ? `Procedure: ${usageDetails.procedureName}\n` : '';
  const patientId = usageDetails?.patientId ? `Patient ID: ${usageDetails.patientId}\n` : '';

  return `----------------------------------
Order Details
----------------------------------
Account # ${accountNumberData || ''}
Account Name: ${accountName || ''}
Order Number: ${orderNumber || ''}
${poNumber}${surgeryDate}${surgeon}${procedure}${patientId}`;
}

export function generateContactInfo(
  config: UserEmailConfiguration,
  salesRepInfo?: SalesRepInfo
): string {
  if (!salesRepInfo) {
    return '';
  }

  const lines: string[] = [];

  const fullName = `${salesRepInfo.firstName ?? ''} ${salesRepInfo.lastName ?? ''}`.trim();
  if (fullName) {
    lines.push(fullName);
  }

  if (salesRepInfo.email) {
    lines.push(salesRepInfo.email);
  }

  if (salesRepInfo.businessRules) {
    const addressData = extractAddressFromBusinessRules(salesRepInfo.businessRules);
    const addressLines = formatAddressLines(addressData);
    lines.push(...addressLines);
  }

  if (!lines.length) {
    return '';
  }

  return [
    '---------------------------------',
    'Contact Information',
    '---------------------------------',
    ...lines,
  ].join('\n');
}

export function generateAttachedBillSheet(usageDetails: any, emailConfig?: { addBillSheetAttachment?: boolean }): string {
  if (!emailConfig?.addBillSheetAttachment) {
    return '';
  }
  const surgeonName = getSurgeonDisplayName(usageDetails);
  const displaySurgeonName = surgeonName !== '--' ? surgeonName : '';
  const procedure = usageDetails?.procedureName || '';
  const surgeryDate = usageDetails?.eventDateTime
    ? moment(new Date(usageDetails.eventDateTime)).format('MM/DD/YYYY')
    : '';
  return `\nAttached is the bill sheet from ${displaySurgeonName} ${procedure} ${surgeryDate}.`.trim();
}

function buildSignature(userEmailConfig: UserEmailConfiguration, salesRepInfo?: SalesRepInfo): string {
  const signatureData = userEmailConfig;
  
  const nameLine = [
    signatureData.firstName ? `<b style="font-weight: bold; font-size: 20px; color:#FFB500">${signatureData.firstName}</b>` : null,
    signatureData.lastName ? `<b style="font-weight: bold; font-size: 20px;">${signatureData.lastName}</b>` : null
  ].filter(Boolean).join(' ');

  const titleLine = signatureData.title ? `<b style="font-weight: bold; font-size: 20px;">${signatureData.title}</b>` : null;
  const businessLine = signatureData.business ? `<b style="font-weight: bold; font-size: 20px;">${signatureData.business}</b>` : null;
  const departmentLine = signatureData.department ? signatureData.department : null;
  const cityLine = signatureData.city ? signatureData.city : null;
  const phoneLine = signatureData.phone ? `Phone: ${signatureData.phone}` : null;
  const emailLine = signatureData.emailLink ? `<a href="mailto:${signatureData.emailLink}" style="color: #0066cc; text-decoration: underline;">${signatureData.emailLink}</a>` : null;
  const hyperlinkLine = signatureData.hyperlink ? `<a href="${signatureData.hyperlink}" style="color: #0066cc; text-decoration: underline;">${signatureData.hyperlink}</a>` : null;

  const lines = [
    nameLine || null,
    titleLine,
    businessLine,
    departmentLine,
    cityLine,
    phoneLine,
    emailLine,
    hyperlinkLine
  ].filter(Boolean) as string[];

  if (!lines.length) return "";

  const block = lines.join("<br>");

  return `<div class="email-signature" style="margin-top: 16px;">${block}</div>`;
}

export function generateEmailHeader(usageSheet: any): string {
  const surgeonName = getSurgeonDisplayName(usageSheet);
  return surgeonName && surgeonName !== '--' ? `Hello ${surgeonName},` : 'Hello,';
}

export function composeEmailFromSpec(baseBody: string, userEmailConfig?: UserEmailConfiguration, usageDetails?: any, salesRepInfo?: SalesRepInfo): string {
  if (!userEmailConfig || Object.keys(userEmailConfig).length === 0) {
    return baseBody;
  }

  let headerIntro = '';
  const emailHeader = generateEmailHeader(usageDetails || {});
  const billSheet = generateAttachedBillSheet(usageDetails || {}, { addBillSheetAttachment: (userEmailConfig as any).addBillSheetAttachment });
  if (emailHeader) {
    headerIntro += `${emailHeader}\n`;
  }
  if (billSheet) {
    headerIntro += billSheet;
  }

  const customBody = userEmailConfig.customEmailBody && userEmailConfig.customEmailBody.trim() ? userEmailConfig.customEmailBody.trim() : '';

  let orderDetails = '';
  if (userEmailConfig.orderDetailsInfo) {
    orderDetails = generateBasicOrderInfo(
      usageDetails?.accountNumber || '',
      usageDetails?.accountName || '',
      usageDetails || {}
    );
  }

  let contactInfo = '';
  if (userEmailConfig.contactDetailsInfo) {
    contactInfo = `\n\n${generateContactInfo(userEmailConfig, salesRepInfo)}`;
  }

  const signature = buildSignature(userEmailConfig, salesRepInfo);

  return [
    headerIntro,
    customBody,
    signature,
    contactInfo,
    orderDetails,
  ].filter(Boolean).join('\n\n').replace(/\n{3,}/g, '\n\n');
}
